import { useEffect, useState, useCallback, Fragment } from "react";
import { Plus, Trash, Receipt, Package, X, CaretDown, CreditCard } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, StatusBadge, StatCard, MobileRow } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useCurrency } from "@/context/CurrencyContext";
import { COUNTRIES } from "@/lib/countries";

const STATUSES = ["draft", "sent", "partially_received", "received", "cancelled"];
const OTHER = "__other";
const BLANK_LINE = { item_id: "", item_name: "", quantity: 1, unit: "pcs", unit_price: 0 };

const money = (n, cur) =>
  `${CURRENCY_SYMBOLS[(cur || "usd").toLowerCase()] || "$"}${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const CURRENCY_SYMBOLS = {
  usd: "$", eur: "€", gbp: "£", inr: "₹", aed: "AED ", cad: "C$", aud: "A$", jpy: "¥",
};

export default function Orders() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [partners, setPartners] = useState([]);
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("all");
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [tariffMeta, setTariffMeta] = useState(null);
  const [landed, setLanded] = useState({ origin: "none", category: "steel", result: null });
  const [form, setForm] = useState({
    supplier_pick: "", supplier_new: "",
    expected_date: "", notes: "", tax_pct: 0, shipping_cost: 0,
    currency: "",
    lines: [{ ...BLANK_LINE }],
  });
  const supplierName = form.supplier_pick === OTHER ? form.supplier_new : form.supplier_pick;
  const { base_currency: baseCurrency, home_country: homeCountry, currencies } = useCurrency();
  const canCreate = user?.role === "admin" || user?.role === "buyer";
  const canReceive = ["admin", "buyer", "vendor"].includes(user?.role);

  const load = useCallback(async () => {
    const [o, p, i] = await Promise.all([
      api.get("/orders", { params: { status: tab } }),
      api.get("/partners"),
      api.get("/items"),
    ]);
    setOrders(o.data);
    setPartners(p.data);
    setItems(i.data);
  }, [tab]);

  useEffect(() => {
    load();
    api.get("/tariffs/meta").then(({ data }) => setTariffMeta(data)).catch(() => {});
  }, [load]);

  const setLine = (idx, patch) =>
    setForm((f) => ({
      ...f,
      lines: f.lines.map((l, i) => (i === idx ? { ...l, ...patch } : l)),
    }));

  const pickLineItem = (idx, id) => {
    const item = items.find((i) => i.id === id);
    setLine(idx, {
      item_id: id,
      item_name: item?.name || "",
      unit: item?.unit || "pcs",
      unit_price: item?.unit_cost ?? 0,
    });
  };

  const subtotal = form.lines.reduce(
    (sum, l) => sum + Number(l.quantity || 0) * Number(l.unit_price || 0),
    0
  );
  const taxAmount = (subtotal * Number(form.tax_pct || 0)) / 100;
  const grandTotal = subtotal + taxAmount + Number(form.shipping_cost || 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!supplierName.trim()) {
      toast.error("Pick a supplier or type a new one");
      return;
    }
    try {
      await api.post("/orders", {
        supplier_name: supplierName.trim(),
        expected_date: form.expected_date || null,
        notes: form.notes,
        tax_pct: Number(form.tax_pct),
        shipping_cost: Number(form.shipping_cost),
        currency: form.currency || baseCurrency,
        lines: form.lines
          .filter((l) => l.item_name && Number(l.quantity) > 0)
          .map((l) => ({
            item_id: l.item_id || null,
            item_name: l.item_name,
            quantity: Number(l.quantity),
            unit: l.unit,
            unit_price: Number(l.unit_price),
          })),
      });
      toast.success("Purchase order raised");
      setOpen(false);
      setForm({ supplier_pick: "", supplier_new: "", expected_date: "", notes: "", tax_pct: 0, shipping_cost: 0, lines: [{ ...BLANK_LINE }] });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/orders/${id}/status`, { status });
      toast.success(`Order marked ${status.replace(/_/g, " ")}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const receiveAll = async (order) => {
    try {
      await api.post(`/orders/${order.id}/receive`, { lines: [] });
      toast.success("Received — stock added to inventory");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const payOnline = async (order) => {
    try {
      const { data } = await api.post("/payments/checkout", {
        order_id: order.id,
        origin_url: window.location.origin,
        user_id: user?.id,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/orders/${id}`);
      toast.success("Purchase order deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const openCount = orders.filter((o) =>
    ["draft", "sent", "partially_received"].includes(o.status)
  ).length;
  const openValue = orders
    .filter((o) => ["draft", "sent", "partially_received"].includes(o.status))
    .reduce((s, o) => s + Number(o.total || 0), 0);

  const lineSummary = (o) =>
    `${o.lines?.length || 0} line${(o.lines?.length || 0) === 1 ? "" : "s"}`;

  const landedCost = async (order) => {
    try {
      const { data } = await api.post("/tariffs/quote", {
        dest_country: homeCountry || "US",
        origin_country: landed.origin === "none" ? null : landed.origin,
        freight_cost: Number(order.shipping_cost) || 0,
        currency: order.currency || "usd",
        lines: (order.lines || []).map((l) => ({
          description: l.item_name, category: landed.category,
          value: Number(l.quantity) * Number(l.unit_price), quantity: Number(l.quantity) || 1,
        })),
      });
      setLanded((s) => ({ ...s, result: { ...data, orderId: order.id } }));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="orders-page">
      <PageHeader
        overline="Procurement"
        title="Purchase Orders"
        description="Raise a PO with multiple line items, send it to the supplier, then receive it — received quantities land straight in inventory."
        action={
          canCreate ? (
            <Button
              data-testid="add-order-button"
              onClick={() => setOpen(true)}
              className="h-11 w-full rounded-none bg-[#002FA7] text-xs font-semibold uppercase tracking-widest hover:bg-[#00227A] sm:h-10 sm:w-auto"
            >
              <Plus size={16} className="mr-2" /> New order
            </Button>
          ) : null
        }
      />

      <div className="mb-5 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <StatCard testid="orders-stat-total" label="Orders" value={orders.length} icon={Receipt} />
        <StatCard testid="orders-stat-open" label="Open orders" value={openCount} icon={Package} tone="primary" sub="draft, sent or partial" />
        <StatCard testid="orders-stat-open-value" label="Open value" value={money(openValue)} icon={Receipt} tone="primary" />
        <StatCard
          testid="orders-stat-received"
          label="Received"
          value={orders.filter((o) => o.status === "received").length}
          icon={Package}
          tone="success"
        />
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mb-4 min-w-0">
        <TabsList className="tab-strip no-scrollbar rounded-none bg-slate-100">
          <TabsTrigger data-testid="order-filter-all" value="all" className="rounded-none text-xs uppercase tracking-wider">All</TabsTrigger>
          {STATUSES.map((s) => (
            <TabsTrigger key={s} data-testid={`order-filter-${s}`} value={s} className="rounded-none text-xs uppercase tracking-wider">
              {s.replace(/_/g, " ")}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {orders.length === 0 ? (
        <EmptyState testid="orders-empty" title="No purchase orders" description="Raise your first PO to a supplier." />
      ) : (
        <>
          <div className="border border-border bg-white md:hidden" data-testid="orders-cards">
            {orders.map((o) => (
              <MobileRow
                key={o.id}
                testid={`order-card-${o.id}`}
                title={`${o.po_number} · ${o.supplier_name}`}
                badges={<StatusBadge testid={`order-status-${o.id}`} value={o.status} />}
                fields={[
                  { label: "Lines", value: lineSummary(o) },
                  { label: "Total", value: money(o.total) },
                  { label: "Expected", value: o.expected_date || "—" },
                  { label: "Raised by", value: o.creator_name },
                ]}
                media={
                  <div className="mt-3 space-y-2">
                    <ul className="divide-y divide-border border border-border">
                      {(o.lines || []).map((l, i) => (
                        <li key={i} className="flex items-center justify-between gap-2 px-2.5 py-2 text-xs">
                          <span className="min-w-0 truncate text-[#0F172A]">{l.item_name}</span>
                          <span className="shrink-0 font-mono text-slate-500">
                            {l.received_quantity || 0}/{l.quantity} {l.unit} · {money(l.unit_price)}
                          </span>
                        </li>
                      ))}
                    </ul>
                    {canReceive && o.status !== "received" && o.status !== "cancelled" && (
                      <button
                        data-testid={`receive-order-mobile-${o.id}`}
                        onClick={() => receiveAll(o)}
                        className="flex h-10 w-full items-center justify-center gap-2 border border-border text-xs font-semibold uppercase tracking-widest text-[#0B7A5B] active:bg-[#E6F7F1]"
                      >
                        <Package size={15} /> Receive all
                      </button>
                    )}
                    {canReceive && (
                      <Select value={o.status} onValueChange={(v) => setStatus(o.id, v)}>
                        <SelectTrigger data-testid={`order-status-select-mobile-${o.id}`} className="h-10 w-full rounded-none border-slate-300 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                }
              />
            ))}
          </div>

          <div className="hidden overflow-x-auto border border-border bg-white md:block">
            <table className="w-full min-w-[900px] text-sm" data-testid="orders-table">
              <thead>
                <tr className="border-b border-border bg-slate-50 text-left">
                  {["PO", "Supplier", "Lines", "Subtotal", "Tax", "Total", "Expected", "Status", "Action"].map((h) => (
                    <th key={h} className="overline px-3 py-2.5 text-slate-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <Fragment key={o.id}>
                    <tr data-testid={`order-row-${o.id}`} className="border-b border-border transition-colors duration-150 hover:bg-slate-50">
                      <td className="px-3 py-2.5">
                        <button
                          data-testid={`order-toggle-${o.id}`}
                          onClick={() => setExpanded(expanded === o.id ? null : o.id)}
                          className="flex items-center gap-1.5 font-mono text-xs font-semibold text-[#002FA7]"
                        >
                          <CaretDown size={12} className={expanded === o.id ? "rotate-180" : ""} />
                          {o.po_number}
                        </button>
                      </td>
                      <td className="px-3 py-2.5 font-medium text-[#0F172A]">{o.supplier_name}</td>
                      <td className="px-3 py-2.5 text-xs text-slate-500">{lineSummary(o)}</td>
                      <td className="px-3 py-2.5 font-mono text-xs text-slate-500">{money(o.subtotal)}</td>
                      <td className="px-3 py-2.5 font-mono text-xs text-slate-500">{money(o.tax_amount)}</td>
                      <td data-testid={`order-total-${o.id}`} className="px-3 py-2.5 font-mono text-xs font-bold text-[#0F172A]">
                        {money(o.total, o.currency)}
                        {o.currency && o.currency !== baseCurrency && (
                          <span className="block font-normal text-slate-400">
                            ≈ {money(o.total_base, baseCurrency)}
                          </span>
                        )}
                        <span
                          data-testid={`order-payment-${o.id}`}
                          className={`mt-0.5 block text-[0.65rem] font-semibold uppercase tracking-wider ${
                            o.payment_status === "paid" ? "text-[#0B7A5B]" : "text-slate-400"
                          }`}
                        >
                          {o.payment_status || "unpaid"}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-xs text-slate-500">{o.expected_date || "—"}</td>
                      <td className="px-3 py-2.5"><StatusBadge testid={`order-status-${o.id}`} value={o.status} /></td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-1.5">
                          {canReceive && (
                            <Select value={o.status} onValueChange={(v) => setStatus(o.id, v)}>
                              <SelectTrigger data-testid={`order-status-select-${o.id}`} className="h-8 w-36 rounded-none border-slate-300 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          )}
                          {canReceive && o.status !== "received" && o.status !== "cancelled" && (
                            <button
                              data-testid={`receive-order-${o.id}`}
                              onClick={() => receiveAll(o)}
                              title="Receive all outstanding"
                              className="border border-border p-1.5 text-[#0B7A5B] transition-colors duration-150 hover:bg-[#E6F7F1]"
                            >
                              <Package size={14} />
                            </button>
                          )}
                          {o.payment_status !== "paid" && (
                            <button
                              data-testid={`pay-order-${o.id}`}
                              onClick={() => payOnline(o)}
                              title="Pay online"
                              className="border border-border p-1.5 text-[#002FA7] transition-colors duration-150 hover:bg-[#EEF2FF]"
                            >
                              <CreditCard size={14} />
                            </button>
                          )}
                          {user?.role === "admin" && (
                            <button
                              data-testid={`delete-order-${o.id}`}
                              onClick={() => remove(o.id)}
                              className="border border-border p-1.5 text-[#FF3B30] transition-colors duration-150 hover:bg-[#FFECEB]"
                            >
                              <Trash size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {expanded === o.id && (
                      <tr className="border-b border-border bg-slate-50">
                        <td colSpan={9} className="px-3 py-3">
                          <div data-testid={`order-lines-${o.id}`} className="border border-border bg-white">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="border-b border-border bg-slate-50 text-left">
                                  {["Item", "Ordered", "Received", "Unit price", "Line total"].map((h) => (
                                    <th key={h} className="overline px-3 py-2 text-slate-500">{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {(o.lines || []).map((l, i) => (
                                  <tr key={i} data-testid={`order-line-${o.id}-${i}`} className="border-b border-border last:border-0">
                                    <td className="px-3 py-2 font-medium text-[#0F172A]">{l.item_name}</td>
                                    <td className="px-3 py-2 font-mono">{l.quantity} {l.unit}</td>
                                    <td className={`px-3 py-2 font-mono ${Number(l.received_quantity) >= Number(l.quantity) ? "text-[#0B7A5B]" : "text-slate-500"}`}>
                                      {l.received_quantity || 0} {l.unit}
                                    </td>
                                    <td className="px-3 py-2 font-mono">{money(l.unit_price)}</td>
                                    <td className="px-3 py-2 font-mono">{money(Number(l.quantity) * Number(l.unit_price))}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            {o.notes && <p className="border-t border-border p-3 text-xs italic text-slate-500">{o.notes}</p>}
                            <div className="border-t border-border p-3">
                              <div className="flex flex-wrap items-end gap-2">
                                <div>
                                  <Label className="text-[0.65rem]">Imported from</Label>
                                  <Select value={landed.origin || "none"} onValueChange={(v) => setLanded({ ...landed, origin: v })}>
                                    <SelectTrigger data-testid={`landed-origin-${o.id}`} className="h-8 w-40 rounded-none border-slate-300 text-xs"><SelectValue placeholder="Country" /></SelectTrigger>
                                    <SelectContent className="max-h-64">
                                      <SelectItem value="none">Not imported</SelectItem>
                                      {COUNTRIES.map(([c, n]) => <SelectItem key={c} value={c}>{n}</SelectItem>)}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div>
                                  <Label className="text-[0.65rem]">Duty category</Label>
                                  <Select value={landed.category} onValueChange={(v) => setLanded({ ...landed, category: v })}>
                                    <SelectTrigger data-testid={`landed-category-${o.id}`} className="h-8 w-40 rounded-none border-slate-300 text-xs"><SelectValue /></SelectTrigger>
                                    <SelectContent className="max-h-64">{(tariffMeta?.categories || []).map((c) => <SelectItem key={c} value={c}>{tariffMeta.category_labels[c]}</SelectItem>)}</SelectContent>
                                  </Select>
                                </div>
                                <Button size="sm" variant="outline" data-testid={`landed-cost-button-${o.id}`} disabled={landed.origin === "none"} onClick={() => landedCost(o)}>
                                  Landed cost with duty
                                </Button>
                              </div>
                              {landed.result?.orderId === o.id && (
                                <p data-testid={`landed-result-${o.id}`} className="mt-2 text-xs text-slate-700">
                                  Goods {money(landed.result.goods_total, o.currency)} · duty {money(landed.result.duty_total, o.currency)} ({landed.result.duty_effective_pct}%) · VAT {money(landed.result.vat_total, o.currency)} · freight {money(landed.result.freight_total, o.currency)} ·{" "}
                                  <strong>landed {money(landed.result.landed_total, o.currency)}</strong>
                                  {landed.result.unmatched_lines > 0 && " — some lines had no rate on file"}
                                </p>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">New purchase order</DialogTitle>
            <DialogDescription>Add as many line items as the order needs.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4" data-testid="add-order-form">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label className="overline text-slate-500">Supplier</Label>
                <Select
                  value={form.supplier_pick}
                  onValueChange={(v) => setForm((f) => ({ ...f, supplier_pick: v }))}
                >
                  <SelectTrigger data-testid="order-supplier-select" className="mt-1.5 rounded-none border-slate-300">
                    <SelectValue placeholder="Pick a supplier" />
                  </SelectTrigger>
                  <SelectContent>
                    {partners
                      .filter((p) => p.type !== "buyer")
                      .map((p) => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}
                    <SelectItem value={OTHER}>Other (type a new supplier)</SelectItem>
                  </SelectContent>
                </Select>
                {form.supplier_pick === OTHER && (
                  <Input
                    data-testid="order-supplier-input"
                    value={form.supplier_new}
                    onChange={(e) => setForm((f) => ({ ...f, supplier_new: e.target.value }))}
                    placeholder="New supplier name"
                    className="mt-2 rounded-none border-slate-300"
                  />
                )}
                <p className="mt-1.5 text-xs text-slate-400">
                  Choose an existing partner, or pick “Other” to type a new supplier.
                </p>
              </div>
              <div>
                <Label className="overline text-slate-500">Currency</Label>
                <Select
                  value={form.currency || baseCurrency}
                  onValueChange={(v) => setForm((f) => ({ ...f, currency: v }))}
                >
                  <SelectTrigger data-testid="order-currency-select" className="mt-1.5 rounded-none border-slate-300">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(currencies || {}).map(([code, meta]) => (
                      <SelectItem key={code} value={code}>
                        {meta.symbol} {code.toUpperCase()} — {meta.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="overline text-slate-500">Expected date</Label>
                <Input
                  data-testid="order-expected-input"
                  type="date"
                  value={form.expected_date}
                  onChange={(e) => setForm({ ...form, expected_date: e.target.value })}
                  className="mt-1.5 rounded-none border-slate-300"
                />
              </div>
            </div>

            <div className="border border-border">
              <div className="flex items-center justify-between border-b border-border bg-slate-50 px-3 py-2">
                <span className="overline text-slate-500">Line items</span>
                <button
                  type="button"
                  data-testid="add-line-button"
                  onClick={() => setForm((f) => ({ ...f, lines: [...f.lines, { ...BLANK_LINE }] }))}
                  className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-[#002FA7]"
                >
                  <Plus size={13} /> Add line
                </button>
              </div>
              <div className="space-y-3 p-3">
                {form.lines.map((l, idx) => (
                  <div key={idx} data-testid={`order-line-row-${idx}`} className="border border-dashed border-slate-300 p-3">
                    <div className="mb-2 flex items-center justify-between">
                      <span className="overline text-slate-400">Line {idx + 1}</span>
                      {form.lines.length > 1 && (
                        <button
                          type="button"
                          data-testid={`remove-line-${idx}`}
                          onClick={() => setForm((f) => ({ ...f, lines: f.lines.filter((_, i) => i !== idx) }))}
                          aria-label="Remove line"
                          className="text-[#FF3B30]"
                        >
                          <X size={14} />
                        </button>
                      )}
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="sm:col-span-2">
                        <Label className="overline text-slate-500">Inventory item</Label>
                        <Select value={l.item_id} onValueChange={(v) => pickLineItem(idx, v)}>
                          <SelectTrigger data-testid={`line-item-select-${idx}`} className="mt-1.5 rounded-none border-slate-300">
                            <SelectValue placeholder="Pick an item (optional)" />
                          </SelectTrigger>
                          <SelectContent>
                            {items.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Input
                          data-testid={`line-name-input-${idx}`}
                          value={l.item_name}
                          onChange={(e) => setLine(idx, { item_name: e.target.value })}
                          placeholder="Or type an item name"
                          className="mt-2 rounded-none border-slate-300"
                        />
                      </div>
                      <div>
                        <Label className="overline text-slate-500">Quantity</Label>
                        <Input data-testid={`line-quantity-input-${idx}`} type="number" min="0" step="any" value={l.quantity} onChange={(e) => setLine(idx, { quantity: e.target.value })} className="mt-1.5 rounded-none border-slate-300" />
                      </div>
                      <div>
                        <Label className="overline text-slate-500">Unit</Label>
                        <Input data-testid={`line-unit-input-${idx}`} value={l.unit} onChange={(e) => setLine(idx, { unit: e.target.value })} className="mt-1.5 rounded-none border-slate-300" />
                      </div>
                      <div>
                        <Label className="overline text-slate-500">Unit price</Label>
                        <Input data-testid={`line-price-input-${idx}`} type="number" min="0" step="0.01" value={l.unit_price} onChange={(e) => setLine(idx, { unit_price: e.target.value })} className="mt-1.5 rounded-none border-slate-300" />
                      </div>
                      <div className="flex items-end">
                        <span data-testid={`line-total-${idx}`} className="font-mono text-sm font-bold text-[#0F172A]">
                          {money(Number(l.quantity || 0) * Number(l.unit_price || 0))}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label className="overline text-slate-500">Tax %</Label>
                <Input data-testid="order-tax-input" type="number" min="0" step="0.01" value={form.tax_pct} onChange={(e) => setForm({ ...form, tax_pct: e.target.value })} className="mt-1.5 rounded-none border-slate-300" />
              </div>
              <div>
                <Label className="overline text-slate-500">Shipping cost</Label>
                <Input data-testid="order-shipping-input" type="number" min="0" step="0.01" value={form.shipping_cost} onChange={(e) => setForm({ ...form, shipping_cost: e.target.value })} className="mt-1.5 rounded-none border-slate-300" />
              </div>
            </div>

            <div className="border border-border bg-slate-50 p-3">
              <div className="flex justify-between text-xs text-slate-500">
                <span>Subtotal</span>
                <span data-testid="order-form-subtotal" className="font-mono">{money(subtotal)}</span>
              </div>
              <div className="mt-1 flex justify-between text-xs text-slate-500">
                <span>Tax</span>
                <span className="font-mono">{money(taxAmount)}</span>
              </div>
              <div className="mt-2 flex justify-between border-t border-border pt-2 text-sm font-bold text-[#0F172A]">
                <span>Total</span>
                <span data-testid="order-form-total" className="font-mono">{money(grandTotal)}</span>
              </div>
            </div>

            <div>
              <Label className="overline text-slate-500">Notes</Label>
              <Textarea data-testid="order-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5 rounded-none border-slate-300" />
            </div>

            <DialogFooter className="mobile-sticky-footer">
              <Button data-testid="save-order-button" type="submit" className="h-11 w-full rounded-none bg-[#002FA7] text-xs font-semibold uppercase tracking-widest hover:bg-[#00227A] sm:h-10 sm:w-auto">
                Raise order
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
